'use client';

import { useEffect } from 'react';
import { reportClientError } from '@/lib/report-client-error';

/**
 * Global client-error capture, mounted once at the app root.
 *
 * Bridges three signals into the unified `/api/client-error` sink so
 * we get a single per-replica server log per user-side failure:
 *
 *   1. **`window.error`** — uncaught synchronous exceptions outside
 *      the React tree (e.g. from a vanilla event handler, a top-level
 *      script, or a third-party SDK initialiser). Without this, the
 *      class of bug "third-party SDK threw and the error boundary
 *      never saw it" would still go unreported.
 *
 *   2. **`window.unhandledrejection`** — any `await` / `.then(...)`
 *      chain that never landed. Particularly relevant for the
 *      `wallet-connect → portfolio fetch → bigint coercion` path that
 *      bit us before; an unhandled rejection there used to escape
 *      every boundary.
 *
 *   3. **`console.error` patch** — React 18 calls
 *      `console.error('The above error occurred in the <X> component …')`
 *      *before* invoking the nearest error boundary. The boundary's
 *      `error.tsx` receives only `{ error, reset }`, with no
 *      `componentStack`. We hook the console call to capture the
 *      stack in a per-tick ring (cleared on the next macrotask) and
 *      forward it alongside the report. This is the single most
 *      useful piece of context for diagnosing #185 — it's the
 *      *component* stack that tells us which provider / hook /
 *      feature is causing the loop, regardless of how minified the
 *      JS function names are.
 *
 *      The patch is conservative: it never throws, always calls the
 *      original implementation, and de-installs on unmount in dev so
 *      Fast Refresh doesn't leak handlers.
 *
 *   The `[global-error-boundary]` and `[lobby/error-boundary]` log
 *   lines (from `app/global-error.tsx` / `app/(lobby)/error.tsx`)
 *   are also picked up here so the boundary's own structured payload
 *   reaches the server even on browsers where the boundary's
 *   `useEffect` never fires (e.g. when `reset()` runs synchronously).
 *
 *   Strict-mode / double-invocation safety lives in
 *   `reportClientError` itself (5s fingerprint dedupe) — this
 *   component does not need its own dedupe.
 */
export function ErrorTrap() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Guard against double-install across HMR / strict mode.
    interface PatchedConsole {
      __takeoverErrorTrapInstalled?: boolean;
      __takeoverErrorTrapOriginal?: typeof console.error;
    }
    const consoleRef = console as unknown as PatchedConsole;
    if (consoleRef.__takeoverErrorTrapInstalled) {
      return;
    }
    consoleRef.__takeoverErrorTrapInstalled = true;
    const originalConsoleError = console.error.bind(console);
    consoleRef.__takeoverErrorTrapOriginal = originalConsoleError;

    // React 18 emits the component stack message on the same
    // microtask the error boundary fires on. We stash it briefly and
    // attach it to the next reportClientError call. The window is
    // very short (next macrotask) because the next unrelated
    // console.error (e.g. a wagmi warning) shouldn't inherit a stale
    // componentStack.
    let pendingComponentStack: string | null = null;
    let pendingClearHandle: ReturnType<typeof setTimeout> | null = null;
    const clearPendingStackSoon = () => {
      if (pendingClearHandle) clearTimeout(pendingClearHandle);
      pendingClearHandle = setTimeout(() => {
        pendingComponentStack = null;
        pendingClearHandle = null;
      }, 0);
    };

    /**
     * React's diagnostic format is one of (varies by version):
     *   console.error("The above error occurred in the <X> component:\n%s\n\n", stack, …)
     *   console.error("The above error occurred in one of your React components:\n%s\n\n", stack)
     * The component stack is in the *second* argument as a `\n`-delimited
     * "in Foo (at file.tsx:42)" tree. We only need to detect the format
     * string; the stack itself is the next arg.
     */
    function tryCaptureReactComponentStack(args: unknown[]): boolean {
      const first = args[0];
      if (typeof first !== 'string') return false;
      if (!first.includes('The above error occurred')) return false;
      const stack = args.find(
        (a) => typeof a === 'string' && a.includes('\n    in '),
      );
      pendingComponentStack =
        typeof stack === 'string' ? stack : first;
      clearPendingStackSoon();
      return true;
    }

    /**
     * The `[global-error-boundary]` and `[lobby/error-boundary]` log
     * entries (emitted by the boundaries' own `useEffect` — see
     * `src/app/global-error.tsx` and `src/app/(lobby)/error.tsx`)
     * carry a structured object with `{ name, message, error, … }`.
     * We forward the whole thing so the server log has both the
     * boundary's narrative and the React internal componentStack
     * captured immediately above.
     */
    function tryForwardBoundaryLog(args: unknown[]): boolean {
      const first = args[0];
      if (typeof first !== 'string') return false;
      if (
        !first.includes('[global-error-boundary]') &&
        !first.includes('[lobby/error-boundary]')
      ) {
        return false;
      }
      const payload = (args[1] ?? {}) as {
        error?: Error;
        message?: string;
        digest?: string;
        name?: string;
      };
      const err =
        payload.error instanceof Error
          ? payload.error
          : new Error(payload.message ?? 'unknown boundary error');
      reportClientError({
        source: first.replace(/[\[\]]/g, '').trim(),
        error: err,
        digest: payload.digest ?? null,
        componentStack: pendingComponentStack,
        extra: { boundaryPayload: payload },
      });
      return true;
    }

    /**
     * Bare `Minified React error #N` hits — e.g. when a third-party
     * library logs the React invariant directly without going through
     * a boundary first.
     */
    function tryCaptureReactInvariant(args: unknown[]): boolean {
      for (const a of args) {
        if (
          a instanceof Error &&
          /Minified React error #\d+/.test(a.message)
        ) {
          reportClientError({
            source: 'console.error.react-invariant',
            error: a,
            componentStack: pendingComponentStack,
          });
          return true;
        }
        if (
          typeof a === 'string' &&
          /Minified React error #\d+/.test(a)
        ) {
          reportClientError({
            source: 'console.error.react-invariant',
            error: new Error(a),
            componentStack: pendingComponentStack,
          });
          return true;
        }
      }
      return false;
    }

    function patchedConsoleError(...args: unknown[]) {
      try {
        if (tryCaptureReactComponentStack(args)) {
          // continue to also forward as a boundary report — but only
          // if the next macrotask's boundary log actually fires. We
          // do *not* forward eagerly here because we want both the
          // componentStack and the structured error.
        } else if (tryForwardBoundaryLog(args)) {
          // already reported with componentStack
        } else {
          tryCaptureReactInvariant(args);
        }
      } catch {
        // never break console.error
      }
      return originalConsoleError(...args);
    }

    console.error = patchedConsoleError as typeof console.error;

    const onWindowError = (event: ErrorEvent) => {
      try {
        const err =
          event.error instanceof Error
            ? event.error
            : new Error(event.message ?? 'window.error');
        reportClientError({
          source: 'window.error',
          error: err,
          componentStack: pendingComponentStack,
          extra: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
          },
        });
      } catch {
        // never break the listener
      }
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        const reason = event.reason;
        const err =
          reason instanceof Error
            ? reason
            : new Error(
                typeof reason === 'string'
                  ? reason
                  : safeJsonStringify(reason) ??
                    'unhandled-promise-rejection',
              );
        reportClientError({
          source: 'window.unhandledrejection',
          error: err,
          componentStack: pendingComponentStack,
          extra: {
            reasonType: typeof reason,
          },
        });
      } catch {
        // never break the listener
      }
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);

    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      // Restore in dev / HMR; production unmount is unusual but
      // leaving the patch in place would be inert anyway because
      // it always calls through to the original.
      if (
        consoleRef.__takeoverErrorTrapOriginal &&
        console.error === patchedConsoleError
      ) {
        console.error = consoleRef.__takeoverErrorTrapOriginal;
      }
      consoleRef.__takeoverErrorTrapInstalled = false;
      if (pendingClearHandle) clearTimeout(pendingClearHandle);
    };
  }, []);

  return null;
}

function safeJsonStringify(value: unknown): string | null {
  try {
    return JSON.stringify(value);
  } catch {
    return null;
  }
}
